import type { ReactNode } from 'react';

import {
  type AvailableCountriesMode,
  DEFAULT_AVAILABLE_COUNTRIES_MODE,
  getAvailableCountriesChoices,
  getAvailableCountriesSelectValue,
  MAX_EXCLUDED_COUNTRY_SELECTIONS,
  resolveAvailableCountriesSelection,
} from '../../../lib/availableCountries';
import { androidLanguages } from '../../../lib/android-languages';
import { normalizeAvailableCountries } from '../../../lib/countries';
import * as Form from '../../Form';
import {
  createSyntheticInputChangeEvent,
  type DappFormInputChangeEvent,
} from '../types';

interface AdditionalInfoSectionBaseProps {
  formData: {
    languages: string[];
    availableCountries: string[];
    availableCountriesMode?: AvailableCountriesMode;
    contactEmail: string;
    supportEmail: string;
  };
  onInputChange: (event: DappFormInputChangeEvent) => void;
  websiteField: ReactNode;
  contactEmailExplanation: string;
  contactEmailRequired?: boolean;
  supportEmailExplanation: string;
  supportEmailRequired?: boolean;
}

export function AdditionalInfoSectionBase({
  formData,
  onInputChange,
  websiteField,
  contactEmailExplanation,
  contactEmailRequired = false,
  supportEmailExplanation,
  supportEmailRequired = false,
}: AdditionalInfoSectionBaseProps) {
  const availableCountriesMode =
    formData.availableCountriesMode ?? DEFAULT_AVAILABLE_COUNTRIES_MODE;
  const isExcludeMode = availableCountriesMode === 'exclude';
  const availableCountriesSelectValue = getAvailableCountriesSelectValue(
    formData.availableCountries,
    availableCountriesMode,
  );
  const availableCountriesInputValue = normalizeAvailableCountries(
    formData.availableCountries,
  );
  const availableCountriesChoices = getAvailableCountriesChoices(
    availableCountriesMode,
  );
  const availableCountriesLabel = isExcludeMode
    ? 'Countries to exclude'
    : 'Countries to include';
  const availableCountriesExplanation = isExcludeMode
    ? 'Start with all countries and exclude the countries where your app should not be available. At least one country must remain available.'
    : 'Choose all countries or limit your app to specific countries.';
  const availableCountriesPlaceholder = isExcludeMode
    ? 'No countries excluded'
    : 'Select countries';
  const availableCountriesSearchPlaceholder = isExcludeMode
    ? 'Search countries to exclude...'
    : 'Search countries...';
  const availableCountriesModeToggleLabel = isExcludeMode
    ? 'Include countries instead'
    : 'Exclude countries instead';

  return (
    <section className="flex flex-col gap-6">
      <Form.SearchableMultiSelect
        label="Language"
        name="languages"
        choices={androidLanguages.map((lang) => ({
          key: lang.code,
          label: lang.name,
        }))}
        placeholder="Select Language"
        searchPlaceholder="Search languages..."
        renderChoice={(choice) => choice.label}
        defaultValues={formData.languages}
        minSelections={1}
        maxVisibleSelections={10}
        required
        onChange={(values) =>
          onInputChange(createSyntheticInputChangeEvent('languages', values))
        }
      />

      <div className="flex flex-col gap-1">
        <input
          type="hidden"
          name="availableCountriesMode"
          value={availableCountriesMode}
          readOnly
        />
        <Form.SearchableMultiSelect
          label={availableCountriesLabel}
          name="availableCountries"
          choices={availableCountriesChoices}
          placeholder={availableCountriesPlaceholder}
          searchPlaceholder={availableCountriesSearchPlaceholder}
          renderChoice={(choice) => choice.label}
          explanation={availableCountriesExplanation}
          defaultValues={availableCountriesSelectValue}
          inputValue={availableCountriesInputValue}
          value={availableCountriesSelectValue}
          minSelections={isExcludeMode ? 0 : 1}
          maxSelections={
            isExcludeMode ? MAX_EXCLUDED_COUNTRY_SELECTIONS : undefined
          }
          maxVisibleSelections={10}
          required
          onChange={(values) =>
            onInputChange(
              createSyntheticInputChangeEvent(
                'availableCountries',
                resolveAvailableCountriesSelection(
                  formData.availableCountries,
                  values,
                  availableCountriesMode,
                ),
              ),
            )
          }
        />
        <button
          type="button"
          className="mt-1 self-start rounded text-left text-sm text-seeker-300 transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-900"
          onClick={() =>
            onInputChange(
              createSyntheticInputChangeEvent(
                'availableCountriesMode',
                isExcludeMode ? 'include' : 'exclude',
              ),
            )
          }
        >
          {availableCountriesModeToggleLabel}
        </button>
      </div>

      {websiteField}

      <Form.Input
        label="Support Email"
        explanation={supportEmailExplanation}
        name="supportEmail"
        type="email"
        defaultValue={formData.supportEmail}
        required={supportEmailRequired}
        onChange={onInputChange}
      />

      <Form.Input
        label="Contact Email"
        explanation={contactEmailExplanation}
        name="contactEmail"
        type="email"
        defaultValue={formData.contactEmail}
        required={contactEmailRequired}
        onChange={onInputChange}
      />
    </section>
  );
}
